import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FaArrowLeft, FaClock, FaBoxOpen, FaPaperPlane, FaFileUpload, FaFilePdf, FaImage, FaDownload, FaCheckCircle, FaFileInvoice } from 'react-icons/fa';
import { toast } from 'sonner';

export default function ProposalThread() {
  const { API, user, BACKEND_URL } = useContext(AppContext);
  const { i18n, t } = useTranslation();
  const { quoteId } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [content, setContent] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/thread?user_id=${user.id}`);
      setQuote(res.data.quote);
      
      const newMessages = res.data.messages || [];
      setMessages(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(newMessages)) {
          return newMessages;
        }
        return prev;
      });
      
      if (loading) setLoading(false);
    } catch (e) {
      console.error('Error loading proposal thread', e);
      if (loading) {
        toast.error('Impossible de charger la proposition');
        navigate('/user/proposals');
      }
    }
  }, [API, user, quoteId, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    fetchThread();
    const interval = setInterval(fetchThread, 5000);
    return () => clearInterval(interval);
  }, [user, quoteId, fetchThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/quotes/${quoteId}/messages?user_id=${user.id}`,
        { content: content.trim() }
      );
      setContent('');
      fetchThread();
    } catch (e) {
      console.error('Error sending message', e);
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Increased limit to 50MB for mobile uploads
    if (file.size > 50 * 1024 * 1024) {
      toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 50MB)' : 'File too large (max 50MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadRes = await axios.post(`${API}/quotes/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const payload = {
        content: i18n.language === 'fr' ? `Pièce jointe : ${uploadRes.data.name}` : `Attachment: ${uploadRes.data.name}`,
        attachments: [uploadRes.data]
      };
      
      await axios.post(`${API}/quotes/${quoteId}/messages?user_id=${user.id}`, payload);
      toast.success('Fichier envoyé');
      fetchThread();
    } catch (error) {
      console.error('Error uploading file:', error);
      const msg = error.response?.data?.detail || 'Erreur upload';
      // Safety check: ensure msg is a string before rendering in toast
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de l\'envoi (détails console)');
    } finally {
      setUploading(false);
    }
  };

  const handleAcceptQuote = async () => {
    if (!confirm(i18n.language === 'fr' ? 'Valider ce devis ? Une commande sera créée automatiquement.' : 'Accept this quote? An order will be created automatically.')) return;
    
    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/accept?user_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Devis validé ! Redirection vers vos commandes...' : 'Quote accepted! Redirecting to your orders...');
      
      // Redirect to orders page after short delay
      setTimeout(() => {
        navigate('/user/orders');
      }, 1500);
    } catch (error) {
      toast.error('Erreur validation');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/quotes/${quoteId}/generate-pdf?user_id=${user.id}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis_${quoteId.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(i18n.language === 'fr' ? 'Devis téléchargé' : 'Quote downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du téléchargement' : 'Download error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">Loading...</div>;
  if (!quote) return null;

  // Check if there is a price offered in the latest vendor message to allow acceptance
  const lastVendorMessage = [...messages].reverse().find(m => m.sender_role === 'vendor' && m.offered_price);
  const canAccept = quote.status !== 'accepted' && quote.status !== 'shipped' && lastVendorMessage;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/user/proposals')}
            className="flex items-center gap-2 text-[#0A2540] hover:underline"
          >
            <FaArrowLeft />
            {i18n.language === 'fr' ? 'Retour aux propositions' : 'Back to proposals'}
          </button>
          
          <div className="flex items-center gap-3">
            {/* Download PDF Button */}
            <Button 
              onClick={handleDownloadPdf} 
              disabled={downloadingPdf}
              variant="outline"
              className="border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF]/10"
            >
              {downloadingPdf ? (
                <span className="animate-pulse">{i18n.language === 'fr' ? 'Génération...' : 'Generating...'}</span>
              ) : (
                <>
                  <FaFileInvoice className="mr-2" />
                  {i18n.language === 'fr' ? 'Télécharger Devis PDF' : 'Download PDF Quote'}
                </>
              )}
            </Button>
            
            {canAccept && (
              <Button onClick={handleAcceptQuote} className="bg-[#635BFF] hover:bg-[#5248d9]">
                <FaCheckCircle className="mr-2" />
                {i18n.language === 'fr' ? 'Valider le devis' : 'Accept Quote'}
              </Button>
            )}
            
            {(quote.status === 'accepted' || quote.status === 'shipped') && (
              <div className="bg-[#635BFF]/10 text-[#5248d9] px-4 py-2 rounded-lg flex items-center">
                <FaCheckCircle className="mr-2" />
                {i18n.language === 'fr' 
                  ? (quote.status === 'shipped' ? 'Expédié' : 'Devis Validé') 
                  : (quote.status === 'shipped' ? 'Shipped' : 'Quote Accepted')}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Summary */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Détails' : 'Details'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Produit' : 'Product'}</p>
                  <div className="flex items-center gap-2 font-semibold text-[#0A2540]">
                    <FaBoxOpen /> {quote.product_name}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Quantité demandée' : 'Requested Quantity'}</p>
                  <p className="font-semibold">{quote.quantity}</p>
                </div>
                {lastVendorMessage && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Dernière offre' : 'Latest Offer'}</p>
                    <p className="text-2xl font-bold text-[#635BFF]">{lastVendorMessage.offered_price} €</p>
                    {lastVendorMessage.delivery_time && (
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FaClock /> {lastVendorMessage.delivery_time}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Chat */}
          <div className="lg:col-span-2">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle>{i18n.language === 'fr' ? 'Discussion avec le fournisseur' : 'Chat with supplier'}</CardTitle>
              </CardHeader>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                      <div className={`rounded-lg px-4 py-3 max-w-[80%] text-sm shadow-sm ${
                        mine ? 'bg-[#0A2540] text-white' : 'bg-white border border-gray-200 text-gray-800'
                      }`}>
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        
                        {m.attachments && m.attachments.map((att, idx) => (
                          <a 
                            key={idx} 
                            href={`${BACKEND_URL}${att.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 mt-2 p-2 rounded ${
                              mine ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {att.type === 'pdf' ? <FaFilePdf /> : <FaImage />}
                            <span className="truncate max-w-[150px]">{att.name}</span>
                            <FaDownload className="ml-auto" />
                          </a>
                        ))}

                        {(m.offered_price || m.delivery_time) && (
                          <div className={`mt-2 pt-2 border-t ${mine ? 'border-white/20' : 'border-gray-200'}`}>
                            {m.offered_price && <div>Price: <strong>{m.offered_price} €</strong></div>}
                            {m.delivery_time && <div>Time: <strong>{m.delivery_time}</strong></div>}
                          </div>
                        )}
                        
                        <p className={`text-[10px] mt-1 ${mine ? 'text-blue-200' : 'text-gray-400'}`}>
                          {formatDate(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white border-t">
                <form onSubmit={handleSend} className="flex gap-2">
                  <label className="cursor-pointer p-2 text-gray-500 hover:text-[#0A2540]">
                    <input 
                      type="file" 
                      accept="image/*,application/pdf,.heic,.heif"
                      className="hidden" 
                      onChange={handleFileUpload} 
                      disabled={uploading} 
                    />
                    <FaFileUpload size={20} />
                  </label>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={1}
                    placeholder={i18n.language === 'fr' ? 'Écrire un message...' : 'Write a message...'}
                    className="flex-1 min-h-[40px] resize-none"
                  />
                  <Button type="submit" disabled={sending || !content.trim()} className="bg-[#635BFF]">
                    <FaPaperPlane />
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
